import React, { useCallback, useContext, useEffect, useState } from 'react';
import Navbar from '../Items/Navbar';
import StudyHubBackNav from '../navigation/StudyHubBackNav';
import AuthContext from '../shared/AuthContext';
import PageMeta from '../seo/PageMeta';
import { REALTIME_INVALIDATE_EVENT } from '../../realtime/realtimeBus';
import { FEATURE } from '../../config/planFeatures';
import { useUserPlan } from '../../hooks/useUserPlan';
import './LeaderboardPage.css';

const WINDOWS = [
  { id: 'weekly', label: 'This week' },
  { id: 'monthly', label: 'This month' },
  { id: 'all_time', label: 'All time' },
];

export default function LeaderboardPage() {
  const { apiClient, user } = useContext(AuthContext);
  const userProfile = user || JSON.parse(localStorage.getItem('userProfile') || 'null');
  const { planReady, canUseFeature } = useUserPlan();
  const [timeWindow, setTimeWindow] = useState('weekly');
  const [entries, setEntries] = useState([]);
  const [me, setMe] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!canUseFeature(FEATURE.LEADERBOARD_RANK)) return;
    setError('');
    try {
      const [boardRes, meRes] = await Promise.all([
        apiClient.get('/api/gamification/leaderboard', { params: { window: timeWindow, limit: 50 } }),
        apiClient.get('/api/gamification/leaderboard/me', { params: { window: timeWindow } }),
      ]);
      setEntries(boardRes.data?.entries || []);
      setMe(meRes.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Leaderboard unavailable');
    }
  }, [apiClient, timeWindow, canUseFeature]);

  useEffect(() => {
    if (!planReady) return;
    load();
  }, [load, planReady]);

  useEffect(() => {
    const onInvalidate = () => load();
    window.addEventListener(REALTIME_INVALIDATE_EVENT, onInvalidate);
    return () => window.removeEventListener(REALTIME_INVALIDATE_EVENT, onInvalidate);
  }, [load]);

  return (
    <div className="leaderboard-page">
      <PageMeta title="Leaderboard" description="QnaRead learning leaderboard by XP" />
      <Navbar userProfile={userProfile} loading={false} />
      <main className="leaderboard-shell">
        <StudyHubBackNav />
        <header className="leaderboard-head">
          <h1>Leaderboard</h1>
          <p>Rankings reflect XP from meaningful learning — practice, review, goals, and progress.</p>
        </header>

        <div className="leaderboard-tabs" role="tablist">
          {WINDOWS.map((w) => (
            <button
              key={w.id}
              type="button"
              role="tab"
              aria-selected={timeWindow === w.id}
              className={timeWindow === w.id ? 'is-active' : ''}
              onClick={() => setTimeWindow(w.id)}
            >
              {w.label}
            </button>
          ))}
        </div>

        {error && <p className="leaderboard-error" role="alert">{error}</p>}

        <ul className="leaderboard-list">
          {entries.map((row) => (
            <li key={`${row.rank}-${row.userId}`} className="leaderboard-row">
              <span className="leaderboard-rank">#{row.rank}</span>
              <span className="leaderboard-name">{row.displayName}</span>
              <span className="leaderboard-meta">Lv {row.level} · {row.xp} XP</span>
            </li>
          ))}
          {!entries.length && !error && (
            <li className="leaderboard-empty">Complete a few learning activities to appear here.</li>
          )}
        </ul>

        {me && (
          <section className="leaderboard-me" aria-label="Your rank">
            <h2>Your rank</h2>
            {me.participating === false ? (
              <p>You opted out or have not earned XP this period yet.</p>
            ) : (
              <>
                <p className="leaderboard-me-rank">{me.rank ? `#${me.rank}` : '—'}</p>
                <p>{me.displayName} · Level {me.level} · {me.xp} XP</p>
                {me.xpToNextRank != null && me.xpToNextRank > 0 && (
                  <p className="leaderboard-me-gap">{me.xpToNextRank} XP to climb one spot</p>
                )}
              </>
            )}
          </section>
        )}
      </main>
    </div>
  );
}
